import {
  HStack,
  IconButton,
  Input,
  InputGroup,
  type InputProps,
  Text,
  type TextProps,
} from "@chakra-ui/react";
import { t } from "@lingui/macro";
import type { FieldState } from "formstate";
import { observer, useLocalObservable } from "mobx-react-lite";
import type { ReactElement } from "react";
import { Icon } from "../Icon";
import { UncollapseOnHover } from "../UncollapseOnHover";
import { EditableTextStore } from "./store";

type Props = {
  field: FieldState<string>;
  onConfirm: (value: string) => Promise<void>;
  isLoading?: boolean;
  inputStyles?: InputProps["sx"];
  textStyles?: TextProps["sx"];
  size?: InputProps["size"];
  customContent?: ReactElement;
  isEditable?: boolean;
};
export const EditableText = observer(function EditableText({
  field,
  onConfirm,
  isLoading,
  inputStyles = {
    minW: "120px",
  },
  size = "md",
  textStyles,
  customContent,
  isEditable = true,
}: Props) {
  const { store } = useLocalObservable(() => ({
    store: new EditableTextStore(),
  }));

  if (!isEditable) {
    return (
      <NonEditableText
        value={field.value}
        textStyles={textStyles}
        customContent={customContent}
      />
    );
  }

  return store.isEditEnabled.value ? (
    <InputGroup>
      <HStack>
        <Input
          sx={inputStyles}
          onChange={({ target }) => {
            field.onChange(target.value);
          }}
          size={size}
          value={field.value}
        />
        <IconButton
          aria-label={t`Text edit cancel`}
          colorScheme="red"
          icon={<Icon name="x-close" />}
          // TODO: Loading state
          isLoading={isLoading}
          onClick={() => {
            field.reset();
            store.isEditEnabled.off();
          }}
          size={size}
          variant="subtle"
        />
        <IconButton
          aria-label={t`Text edit confirm`}
          colorScheme="green"
          icon={<Icon name="check" />}
          // TODO: Loading state
          isLoading={isLoading}
          onClick={async () => {
            await onConfirm(field.value);
            store.isEditEnabled.off();
          }}
          size={size}
          variant="subtle"
        />
      </HStack>
    </InputGroup>
  ) : (
    <UncollapseOnHover
      dataComponentName={store.dataComponentName}
      width="8"
      hiddenComponent={
        <IconButton
          aria-label={t`Text edit trigger`}
          colorScheme="grey"
          icon={<Icon name="edit-02" boxSize="4" />}
          onClick={store.isEditEnabled.on}
          size={size}
          variant="ghost"
        />
      }
    >
      <NonEditableText
        value={field.value}
        textStyles={textStyles}
        customContent={customContent}
      />
    </UncollapseOnHover>
  );
});

type NonEditableTextProps = {
  value: string;
  textStyles?: TextProps["sx"];
  customContent?: ReactElement;
};
const NonEditableText = observer(function NonEditableText({
  value,
  textStyles,
  customContent,
}: NonEditableTextProps) {
  return customContent ? customContent : <Text sx={textStyles}>{value}</Text>;
});
